import { notify } from '@affine/component';
import { OAuth } from '@affine/core/components/affine/auth/oauth';
import { AuthService, ServerService } from '@affine/core/modules/cloud';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { ServerDeploymentType } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { useSelfhostLoginVersionGuard } from '../hooks/affine/use-selfhost-login-version-guard';
import type { SignInState } from '.';
import * as style from './style.css';

type Lang = 'en' | 'es' | 'pt';

const translations = {
  en: {
    headline1: 'Docs for Devs.',
    headline2: 'yoDEV Workplace',
    subtitle:
      'Your collaborative workspace for docs, whiteboards, and databases. Built for teams who want to work smarter together.',
    features: [
      'Block-based document creation (Docs, DB, Tables)',
      'Unlimited workspaces (10 members per workspace)',
      'Invite colleagues and clients',
      'Real-time collaboration with cursor presence',
      'Local and cloud sync',
      'Infinite canvas and whiteboard',
      'AI copilot integration (Claude)',
      'Bidirectional GitHub sync',
    ],
    cta: 'Get organized and collaborate',
    claudeTitle: 'Claude Code Integration',
    claudeItems: [
      'Connect your workspace directly from your terminal',
      'Access your documents without leaving the CLI',
      'Claude Code reads, creates and updates your documents',
      'Search information with natural language',
    ],
    vscodeTitle: 'VS Code Extension',
    vscodeItems: [
      'Access all your documents directly from your editor',
      'Personal notes',
      'Meeting notes',
      'Technical documents',
    ],
    githubTitle: 'GitHub Integration',
    githubItems: [
      'Full bidirectional sync',
      'Edit your technical and team documents in a modern markdown editor',
    ],
    alreadyMember: 'Already a yoDEV member?',
    or: 'or',
    notMember: 'Not a yoDEV member yet? Registration takes just a minute.',
    register: 'Register at yoDEV.dev →',
  },
  es: {
    headline1: 'Docs para Devs.',
    headline2: 'yoDEV Workplace',
    subtitle:
      'Tu espacio colaborativo para documentos, pizarras y bases de datos. Creado para equipos que quieren trabajar de forma m\u00e1s inteligente.',
    features: [
      'Creaci\u00f3n de documentos basada en bloques (Docs, DB, Tablas)',
      'Espacios de trabajo ilimitados (10 miembros por espacio)',
      'Invita a colegas y clientes',
      'Colabora en tiempo real con presencia de cursor',
      'Sincronizaci\u00f3n local y en la nube',
      'Lienzo y pizarra sin bordes',
      'Integraci\u00f3n de copiloto de IA (Claude)',
      'Sincronizaci\u00f3n bidireccional con GitHub',
    ],
    cta: 'Organiza y colabora',
    claudeTitle: 'Integraci\u00f3n con Claude Code',
    claudeItems: [
      'Conecta tu espacio de trabajo directamente desde tu terminal',
      'Accede a tus documentos sin salir de la CLI',
      'Claude Code lee, crea y actualiza tus documentos',
      'Busca informaci\u00f3n con lenguaje natural',
    ],
    vscodeTitle: 'Extensi\u00f3n para VS Code',
    vscodeItems: [
      'Accede a todos tus documentos directamente desde tu editor',
      'Notas personales',
      'Notas de reuniones',
      'Documentos t\u00e9cnicos',
    ],
    githubTitle: 'Integraci\u00f3n con GitHub',
    githubItems: [
      'Sincronizaci\u00f3n bidireccional completa',
      'Edita tus documentos t\u00e9cnicos y de equipo en un editor markdown moderno',
    ],
    alreadyMember: '\u00bfYa eres miembro de yoDEV?',
    or: 'o',
    notMember:
      '\u00bfA\u00fan no eres miembro de yoDEV? El registro toma solo un minuto.',
    register: 'Reg\u00edstrate en yoDEV.dev \u2192',
  },
  pt: {
    headline1: 'Docs para Devs.',
    headline2: 'yoDEV Workplace',
    subtitle:
      'Seu espa\u00e7o colaborativo para documentos, quadros brancos e bancos de dados. Feito para equipes que querem trabalhar de forma mais inteligente.',
    features: [
      'Cria\u00e7\u00e3o de documentos baseada em blocos (Docs, DB, Tabelas)',
      'Espa\u00e7os de trabalho ilimitados (10 membros por espa\u00e7o)',
      'Convide colegas e clientes',
      'Colabore em tempo real com presen\u00e7a de cursor',
      'Sincroniza\u00e7\u00e3o local e na nuvem',
      'Tela e quadro infinito',
      'Integra\u00e7\u00e3o de copiloto de IA (Claude)',
      'Sincroniza\u00e7\u00e3o bidirecional com GitHub',
    ],
    cta: 'Organize e colabore',
    claudeTitle: 'Integra\u00e7\u00e3o com Claude Code',
    claudeItems: [
      'Conecte seu espa\u00e7o de trabalho diretamente do terminal',
      'Acesse seus documentos sem sair da CLI',
      'Claude Code l\u00ea, cria e atualiza seus documentos',
      'Busque informa\u00e7\u00f5es com linguagem natural',
    ],
    vscodeTitle: 'Extens\u00e3o para VS Code',
    vscodeItems: [
      'Acesse todos os seus documentos diretamente do editor',
      'Notas pessoais',
      'Notas de reuni\u00f5es',
      'Documentos t\u00e9cnicos',
    ],
    githubTitle: 'Integra\u00e7\u00e3o com GitHub',
    githubItems: [
      'Sincroniza\u00e7\u00e3o bidirecional completa',
      'Edite seus documentos t\u00e9cnicos e de equipe em um editor markdown moderno',
    ],
    alreadyMember: 'J\u00e1 \u00e9 membro do yoDEV?',
    or: 'ou',
    notMember:
      'Ainda n\u00e3o \u00e9 membro do yoDEV? O registro leva apenas um minuto.',
    register: 'Registre-se em yoDEV.dev \u2192',
  },
} as const;

const langLabels: Record<Lang, string> = {
  en: 'EN',
  es: 'ES',
  pt: 'PT',
};

// yoDEV Landing Page Component — Linear-inspired design system
export const SignInStep = ({
  state,
  changeState,
  onSkip,
  onAuthenticated,
}: {
  state: SignInState;
  changeState: Dispatch<SetStateAction<SignInState>>;
  onSkip: () => void;
  onAuthenticated?: (status: AuthSessionStatus) => void;
}) => {
  const t = useI18n();
  const serverService = useService(ServerService);
  const versionError = useSelfhostLoginVersionGuard(serverService.server);
  const isSelfhosted = useLiveData(
    serverService.server.config$.selector(
      c => c.type === ServerDeploymentType.Selfhosted
    )
  );
  const authService = useService(AuthService);
  const loginStatus = useLiveData(authService.session.status$);

  const [lang, setLang] = useState<Lang>(() => {
    const browserLang = navigator.language.slice(0, 2).toLowerCase();
    if (browserLang === 'es') return 'es';
    if (browserLang === 'pt') return 'pt';
    return 'en';
  });

  const i = translations[lang];

  const handleLangChange = useCallback((newLang: Lang) => {
    setLang(newLang);
  }, []);

  useEffect(() => {
    if (loginStatus === 'authenticated') {
      notify.success({
        title: t['com.affine.auth.toast.title.signed-in'](),
        message: t['com.affine.auth.toast.message.signed-in'](),
      });
    }
    onAuthenticated?.(loginStatus);
  }, [loginStatus, onAuthenticated, t]);

  if (versionError && isSelfhosted) {
    return (
      <div className={style.landingContainer}>
        <div>{versionError}</div>
      </div>
    );
  }

  return (
    <div className={style.landingContainer}>
      {/* Language Selector — top right */}
      <div className={style.langSelector}>
        {(Object.keys(langLabels) as Lang[]).map(l => (
          <button
            key={l}
            className={l === lang ? style.langButtonActive : style.langButton}
            onClick={() => handleLangChange(l)}
          >
            {langLabels[l]}
          </button>
        ))}
      </div>

      {/* Two-column grid: features left, auth right */}
      <div className={style.landingGrid}>
        {/* Left column — hero content */}
        <div className={style.heroColumn}>
          <div className={style.logoRow}>
            <img
              src="https://canada1.discourse-cdn.com/flex009/uploads/inovacon/optimized/1X/0453fb6aa23d6223f27068b8adaf9a1fff850f57_2_180x180.png"
              alt="yoDEV"
              className={style.landingLogo}
            />
          </div>

          <h1 className={style.headlineMain}>{i.headline1}</h1>
          <h1 className={style.headlineAccent}>{i.headline2}</h1>

          <p className={style.landingSubtitle}>{i.subtitle}</p>

          <ul className={style.featureList}>
            {i.features.map((feature, idx) => (
              <li key={idx} className={style.featureItem}>
                <span className={style.featureCheck}>&#10003;</span>
                <span className={style.featureText}>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right column — auth */}
        <div className={style.authColumn}>
          <div className={style.authCard}>
            <p className={style.memberTitle}>{i.alreadyMember}</p>
            <div className={style.oauthWrapper}>
              <OAuth redirectUrl={state.redirectUrl} />
            </div>

            <div className={style.divider}>
              <div className={style.dividerLine} />
              <span className={style.dividerText}>{i.or}</span>
              <div className={style.dividerLine} />
            </div>

            <div className={style.registerSection}>
              <p className={style.registerText}>{i.notMember}</p>
              <a
                href="https://www.yodev.dev/signup"
                target="_blank"
                rel="noreferrer"
                className={style.registerLink}
              >
                {i.register}
              </a>
            </div>
          </div>

          {/* Integration tags — compact, below auth card */}
          <div className={style.integrationTags}>
            <div className={style.integrationTag}>
              <span className={style.integrationTagIcon}>&#x2759;&#x5F;</span>
              <div>
                <span className={style.integrationTagTitle}>
                  {i.claudeTitle}
                </span>
                <span className={style.integrationTagDesc}>
                  {i.claudeItems[0]}
                </span>
              </div>
            </div>
            <div className={style.integrationTag}>
              <span className={style.integrationTagIcon}>&lt;/&gt;</span>
              <div>
                <span className={style.integrationTagTitle}>
                  {i.vscodeTitle}
                </span>
                <span className={style.integrationTagDesc}>
                  {i.vscodeItems[0]}
                </span>
              </div>
            </div>
            <div className={style.integrationTag}>
              <span className={style.integrationTagIcon}>&#x2693;</span>
              <div>
                <span className={style.integrationTagTitle}>
                  {i.githubTitle}
                </span>
                <span className={style.integrationTagDesc}>
                  {i.githubItems[0]}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
